import { useState } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import {
  LayoutDashboard, TrendingUp, ShoppingCart, CloudSun, Sparkles, Package,
  Target, CalendarDays, Trash2, Settings as Cog, LogOut, ChevronDown,
  ShieldCheck, ArrowUpRight, Lock
} from "lucide-react";

const NAV = [
  { to: "/app", end: true, icon: LayoutDashboard, label: "Dashboard", feature: null }, // always available
  { to: "/app/forecast", icon: TrendingUp, label: "Forecast", feature: "forecast" },
  { to: "/app/purchasing", icon: ShoppingCart, label: "Purchasing", feature: "purchasing" },
  { to: "/app/insights", icon: Sparkles, label: "AI Insights", feature: "insights" },
  { to: "/app/weather", icon: CloudSun, label: "Weather Intel", feature: "weather" },
  { to: "/app/accuracy", icon: Target, label: "Forecast Accuracy", feature: "accuracy" },
  { to: "/app/inventory", icon: Package, label: "Inventory", feature: "inventory" },
  { to: "/app/events", icon: CalendarDays, label: "Events & Holidays", feature: "events" },
  { to: "/app/waste", icon: Trash2, label: "Waste", feature: "waste" },
  { to: "/app/org", icon: LayoutDashboard, label: "Org Dashboard", feature: "multi_store" },
  { to: "/app/settings", icon: Cog, label: "Settings & POS", feature: null }, // always available
];

export default function AppShell() {
  const { user, logout, stores, activeStore, setActiveStore, hasFeature } = useAuth();
  const navigate = useNavigate();
  const store = stores.find((s) => s.id === activeStore);

  const planLabel =
    user?.plan === "multi"
      ? "Multi-Site Enterprise"
      : user?.plan === "pro"
      ? "Pro Plan"
      : user?.plan === "starter"
      ? "Starter Trial"
      : "Free Trial";

  // Locked features stay visible so users can discover them; clicking one
  // prompts an upgrade instead of navigating.
  const lockedNav = NAV.filter((n) => n.feature && !hasFeature(n.feature));
  const upgradeLabel = (feature) => {
    const t = { purchasing: "Pro", insights: "Pro", accuracy: "Pro", events: "Pro", waste: "Pro", pos_integrations: "Pro", multi_store: "Multi-site" }[feature];
    return t ? `Upgrade to ${t}` : "Upgrade your plan";
  };

  return (
    <div className="min-h-screen flex bg-zinc-50 font-sans text-zinc-900">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-zinc-200/90 bg-white flex flex-col fixed h-screen z-30">
        <div className="h-16 flex items-center justify-between px-5 border-b border-zinc-200/90">
          <Logo wordmark="Optics" />
          <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full uppercase tracking-wider">
            v2.4 Live
          </span>
        </div>

        {/* Navigation links */}
        <nav className="flex-1 overflow-y-auto p-3 space-y-1" data-testid="sidebar-nav">
          {NAV.map((n) => {
            const locked = n.feature && !hasFeature(n.feature);
            if (locked) return null;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.label.toLowerCase().replace(/[\s&]+/g, "-")}`}
                className={({ isActive }) =>
                  `relative flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all duration-150 ${
                    isActive
                      ? "bg-zinc-900 text-white shadow-sm"
                      : "text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <n.icon className={`h-4 w-4 shrink-0 ${isActive ? "text-amber-400" : "text-zinc-500"}`} />
                    <span className="truncate">{n.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}

          {/* Locked features — visible but gated behind an upgrade prompt */}
          {lockedNav.length > 0 && (
            <div className="pt-2 mt-2 border-t border-zinc-100">
              <div className="px-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-zinc-400">Upgrade to unlock</div>
              {lockedNav.map((n) => (
                <button
                  key={n.to}
                  data-testid={`nav-locked-${n.feature}`}
                  onClick={() => toast.info(`${n.label} isn't included in your current plan.`, {
                    description: `${upgradeLabel(n.feature)} to unlock ${n.label}.`,
                    action: { label: "View plans", onClick: () => navigate("/app/settings") },
                  })}
                  className="w-full relative flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold text-zinc-400 hover:text-zinc-600 hover:bg-zinc-50 transition-all duration-150"
                >
                  <n.icon className="h-4 w-4 shrink-0 text-zinc-300" />
                  <span className="truncate">{n.label}</span>
                  <Lock className="h-3 w-3 ml-auto shrink-0 text-zinc-300" />
                </button>
              ))}
            </div>
          )}
        </nav>

        {/* Plan & User Profile Area */}
        <div className="p-3 border-t border-zinc-200/90 bg-zinc-50/50 space-y-2">
          {/* Plan badge */}
          <div className="p-2.5 rounded-xl bg-white border border-zinc-200/80 shadow-2xs">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold text-zinc-800 flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> {planLabel}
              </span>
              <Link
                to="/app/settings"
                className="text-[10px] font-bold text-amber-700 hover:underline flex items-center gap-0.5"
              >
                Upgrade <ArrowUpRight className="h-3 w-3" />
              </Link>
            </div>
            <div className="text-[10px] text-zinc-500 mt-1">14 days full feature access</div>
          </div>

          {/* User profile */}
          <div className="flex items-center justify-between px-2 py-1.5">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="h-8 w-8 rounded-full bg-zinc-900 text-white grid place-items-center text-xs font-bold uppercase shrink-0 shadow-sm">
                {(user?.name || user?.email || "U").slice(0, 1)}
              </div>
              <div className="text-xs min-w-0">
                <div className="font-semibold text-zinc-900 truncate">{user?.name || "Manager"}</div>
                <div className="text-[11px] text-zinc-400 truncate">{user?.email}</div>
              </div>
            </div>

            <button
              data-testid="logout-btn"
              title="Sign out"
              onClick={() => {
                navigate("/");
                logout();
              }}
              className="p-1.5 text-zinc-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 ml-64 flex flex-col min-w-0">
        {/* Top Navbar */}
        <header className="h-16 border-b border-zinc-200/90 bg-white/80 backdrop-blur-xl sticky top-0 z-20 flex items-center justify-between px-8">
          <div className="flex items-center gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">Active Store</div>
              <div className="font-display font-semibold text-zinc-900 text-sm flex items-center gap-2">
                <span>{store?.name || "My Store"}</span>
                <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> POS Synced
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {stores.length > 1 && (
              <div className="relative">
                <select
                  data-testid="store-switcher"
                  value={activeStore || ""}
                  onChange={(e) => setActiveStore(e.target.value)}
                  className="appearance-none text-xs font-semibold border border-zinc-200 rounded-xl pl-3 pr-8 py-2 bg-white hover:bg-zinc-50 focus:outline-none focus:ring-2 focus:ring-zinc-900 transition-colors"
                >
                  {stores.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} ({s.city})
                    </option>
                  ))}
                </select>
                <ChevronDown className="h-3.5 w-3.5 text-zinc-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            )}

            <Link
              to="/app/forecast"
              className="inline-flex items-center gap-1.5 text-xs font-semibold bg-zinc-900 text-white hover:bg-zinc-800 px-3.5 py-2 rounded-xl transition-all shadow-sm"
            >
              <TrendingUp className="h-3.5 w-3.5 text-amber-400" /> View Forecast
            </Link>
          </div>
        </header>

        {/* Page body */}
        <main className="flex-1 p-8 max-w-7xl w-full">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
